import React, { useState } from 'react'
import { RgbColorPicker } from 'react-colorful'
import { Box, Popover, TextField, Typography, styled } from '@mui/material'
import { type CustomPaletteColor, useTheme } from '@mui/material/styles'

import { TextField as CustomTextField } from '~components/common'

interface ColorInputProps {
  name: string
  label: string
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

interface RgbColor {
  r: number
  g: number
  b: number
}

const ColorInputLabelStyled = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 16,
  letterSpacing: theme.typography.body2.letterSpacing,
  fontFamily: theme.typography.fontFamily,
  '&.Mui-focused': {
    color: theme.palette.text.primary,
  },
}))

const hexTorgb = (hex: string): RgbColor => {
  const cleanHex = hex.replace('#', '').padEnd(6, '0')
  const r = parseInt(cleanHex.substring(0, 2), 16) || 0
  const g = parseInt(cleanHex.substring(2, 4), 16) || 0
  const b = parseInt(cleanHex.substring(4, 6), 16) || 0
  return { r, g, b }
}

const rgbToHex = (rgb: RgbColor): string => {
  const r = Math.round(rgb.r).toString(16).padStart(2, '0')
  const g = Math.round(rgb.g).toString(16).padStart(2, '0')
  const b = Math.round(rgb.b).toString(16).padStart(2, '0')
  return `${r}${g}${b}`.toUpperCase()
}

const rgbToString = (rgb: RgbColor): string => {
  return `rgb(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)})`
}

const ColorInput: React.FC<ColorInputProps> = ({ name, label, value, onChange }) => {
  const [color, setColor] = useState(value)
  const [rgbColor, setRgbColor] = useState<RgbColor>(hexTorgb(value))
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const theme = useTheme()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = event.target.value.toUpperCase()

    const hexRegex = /^[0-9A-F]{0,6}$/

    if (hexRegex.test(inputValue)) {
      setColor(inputValue)
      setRgbColor(hexTorgb(inputValue))
      onChange({
        ...event,
        target: {
          ...event.target,
          name: event.target.name,
          value: inputValue,
        },
      })
    }
  }

  const handleRgbChange = (newRgbColor: RgbColor): void => {
    setRgbColor(newRgbColor)
    const hexValue = rgbToHex(newRgbColor)
    setColor(hexValue)
    onChange({
      target: {
        name,
        value: hexValue,
      },
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const handleRgbInputChange = (channel: keyof RgbColor, value: string): void => {
    const numValue = parseInt(value, 10)

    if (isNaN(numValue)) return

    const clampedValue = Math.max(0, Math.min(255, numValue))

    const newRgbColor = { ...rgbColor, [channel]: clampedValue }
    setRgbColor(newRgbColor)
    const hexValue = rgbToHex(newRgbColor)
    setColor(hexValue)
    onChange({
      target: {
        name,
        value: hexValue,
      },
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <ColorInputLabelStyled
        variant='body2'
        sx={{ color: theme.palette.input.default, mb: 1, fontSize: 15 }}
      >
        {label}
      </ColorInputLabelStyled>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: 'none',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: `0 0 0 1px ${theme.palette.text.secondary}`,
          transition: 'box-shadow 0.2s ease-in-out',
          '&:hover': {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
          },
          '&:focus-within': {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}, 0 0 0 4px ${(theme.palette.primary as CustomPaletteColor).states.focus}`,
          },
        }}
      >
        <Typography
          sx={{
            px: 1,
            py: 0.5,
            bgcolor: '#f5f5f5',
            borderRight: '1px solid #ccc',
            fontWeight: 'bold',
            userSelect: 'none',
          }}
        >
          #
        </Typography>
        <TextField
          name={name}
          value={color}
          onChange={handleChange}
          variant='standard'
          sx={{
            flexGrow: 1,
            '& .MuiInputBase-root': {
              height: '100%',
            },
            '& .MuiInputBase-input': {
              px: 1,
              py: 0.5,
            },
          }}
          slotProps={{
            input: {
              disableUnderline: true,
              endAdornment: (
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: rgbToString(rgbColor),
                    border: '1px solid #ccc',
                    borderRadius: '2px',
                    mr: 1,
                    cursor: 'pointer',
                  }}
                  onClick={handleClick}
                />
              ),
            },
          }}
        />
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: '300px' }}>
          <RgbColorPicker style={{ width: '100%' }} color={rgbColor} onChange={handleRgbChange} />
          <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 1 }}>
            <CustomTextField
              label='R'
              type='number'
              value={Math.round(rgbColor.r)}
              onChange={(e) => handleRgbInputChange('r', e.target.value)}
              inputProps={{ min: 0, max: 255 }}
              size='small'
              sx={{ mt: '0 !important' }}
              containerStyle={{ width: '100%' }}
            />
            <CustomTextField
              label='G'
              type='number'
              value={Math.round(rgbColor.g)}
              onChange={(e) => handleRgbInputChange('g', e.target.value)}
              inputProps={{ min: 0, max: 255 }}
              size='small'
              sx={{ mt: '0 !important' }}
              containerStyle={{ width: '100%' }}
            />
            <CustomTextField
              label='B'
              type='number'
              value={Math.round(rgbColor.b)}
              onChange={(e) => handleRgbInputChange('b', e.target.value)}
              inputProps={{ min: 0, max: 255 }}
              size='small'
              sx={{ mt: '0 !important' }}
              containerStyle={{ width: '100%' }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: 'none',
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: `0 0 0 1px ${theme.palette.text.secondary}`,
              transition: 'box-shadow 0.2s ease-in-out',
              '&:hover': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
              },
              '&:focus-within': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}, 0 0 0 4px ${(theme.palette.primary as CustomPaletteColor).states.focus}`,
              },
            }}
          >
            <Typography
              sx={{
                px: 1,
                py: 0.5,
                bgcolor: '#f5f5f5',
                borderRight: '1px solid #ccc',
                fontWeight: 'bold',
                userSelect: 'none',
              }}
            >
              #
            </Typography>
            <TextField
              name={name}
              value={color}
              onChange={handleChange}
              variant='standard'
              sx={{
                flexGrow: 1,
                '& .MuiInputBase-root': {
                  height: '100%',
                },
                '& .MuiInputBase-input': {
                  px: 1,
                  py: 0.5,
                },
              }}
              slotProps={{
                input: {
                  disableUnderline: true,
                },
              }}
            />
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}

export default ColorInput
